import {
  ApiMasterLabelCrop,
  ApiMasterLabelUPCData,
  DesignLockStatusInfo,
  ForgeDesignGroupReviewSummary,
  ForgeDesignGroupSummary,
  ForgeDesignNote,
  MasterCropData,
  MasterDesignData,
  MasterUPCData,
} from '../types';

function recordValue(record: Record<string, unknown>, camelKey: string, snakeKey: string): unknown {
  return record[camelKey] ?? record[snakeKey];
}

export function transformApiDesignLockStatusInfo(
  value: unknown,
  fallbackDesignId: string
): DesignLockStatusInfo | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const lock = value as Record<string, unknown>;
  const status = lock.status;
  if (status !== 'in_sync' && status !== 'diverged') return undefined;

  return {
    designId: String(recordValue(lock, 'designId', 'design_id') ?? fallbackDesignId),
    status,
    cropsChanged: Boolean(recordValue(lock, 'cropsChanged', 'crops_changed')),
    metadataChanged: Boolean(recordValue(lock, 'metadataChanged', 'metadata_changed')),
    groupsChanged: Boolean(recordValue(lock, 'groupsChanged', 'groups_changed')),
    lockedActiveCropCount: Number(
      recordValue(lock, 'lockedActiveCropCount', 'locked_active_crop_count') ?? 0
    ),
    liveActiveCropCount: Number(
      recordValue(lock, 'liveActiveCropCount', 'live_active_crop_count') ?? 0
    ),
    lockedBy: String(recordValue(lock, 'lockedBy', 'locked_by') ?? ''),
    lockedAt: String(recordValue(lock, 'lockedAt', 'locked_at') ?? ''),
    updatedBy:
      recordValue(lock, 'updatedBy', 'updated_by') == null
        ? null
        : String(recordValue(lock, 'updatedBy', 'updated_by')),
    updatedAt:
      recordValue(lock, 'updatedAt', 'updated_at') == null
        ? null
        : String(recordValue(lock, 'updatedAt', 'updated_at')),
    contentHashVersion: Number(
      recordValue(lock, 'contentHashVersion', 'content_hash_version') ?? 0
    ),
  };
}

function optionalString(value: unknown): string | undefined {
  if (value === null || value === undefined) return undefined;
  const text = String(value).trim();
  return text || undefined;
}

function extractBarcodeFromDesignImageUrls(...urls: Array<unknown>): string | undefined {
  for (const url of urls) {
    const text = optionalString(url);
    if (!text) continue;
    const matches = text.match(/\d{8,14}/g);
    if (!matches || matches.length === 0) continue;
    return matches[matches.length - 1];
  }
  return undefined;
}

function transformCrop(api: ApiMasterLabelCrop): MasterCropData {
  return {
    id: api.cropId,
    imageUrl: api.vizUrl,
    isInactive: !api.isActive,
    isPending: api.isPending,
    isMismatch: api.pendingReason === 'unknown_upc',
    storeId: String(api.storeId),
    itemNumber: api.itemNumber || undefined,
    source: api.source || undefined,
    forgeOriginSource: api.forgeOriginSource || undefined,
    barcode: optionalString(api.barcode),
    normalizedBarcode: optionalString(api.normalizedBarcode),
    normalizedUpc: optionalString(api.normalizedUpc),
    createdAt: api.createdAt || undefined,
    orientation: api.orientation || undefined,
    classificationType: api.classificationType || undefined,
    reviewIssueType: api.reviewIssueType ?? undefined,
    imageResolution: api.imageResolution || undefined,
    createdBy: api.createdBy || undefined,
    updatedAt: api.updatedAt || undefined,
    updatedBy: api.updatedBy || undefined,
  };
}

function transformGroupSummary(api: ForgeDesignGroupSummary): ForgeDesignGroupSummary {
  const summary: ForgeDesignGroupSummary = {
    designId: api.designId,
    normalizedBarcode: optionalString(api.normalizedBarcode) ?? null,
    previewVizUrl: optionalString(api.previewVizUrl) ?? null,
    forgeUrl: optionalString(api.forgeUrl) ?? null,
  };
  if (api.barcodeStatus) summary.barcodeStatus = api.barcodeStatus;
  if (api.cropCount !== undefined && api.cropCount !== null) summary.cropCount = api.cropCount;
  if (api.noteCount !== undefined && api.noteCount !== null) summary.noteCount = api.noteCount;
  if (api.createdAt) summary.createdAt = api.createdAt;
  if (api.updatedAt) summary.updatedAt = api.updatedAt;
  if (api.updatedBy) summary.updatedBy = api.updatedBy;
  return summary;
}

function transformGroupReview(
  review?: ForgeDesignGroupReviewSummary | null
): ForgeDesignGroupReviewSummary | null {
  if (!review?.groupId) return null;
  return {
    groupId: review.groupId,
    isReviewed: Boolean(review.isReviewed),
    reviewedBy: review.reviewedBy ?? null,
    reviewedAt: review.reviewedAt ?? null,
  };
}

function transformDesignNotes(notes?: ForgeDesignNote[]): ForgeDesignNote[] {
  return (notes ?? [])
    .map((note) => ({
      id: note.id ?? null,
      text: optionalString(note.text) || '',
      createdBy: note.createdBy ?? null,
      createdAt: note.createdAt ?? null,
    }))
    .filter((note) => note.text.length > 0);
}

export function transformApiUPCData(api: ApiMasterLabelUPCData): MasterUPCData {
  const actualItemNumber = optionalString(api.itemNumber) ?? null;
  const lookupNormalizedBarcode = optionalString(api.lookupNormalizedBarcode);
  const columnKey =
    optionalString(api.columnKey) ||
    actualItemNumber ||
    (lookupNormalizedBarcode ? `nb:${lookupNormalizedBarcode}` : undefined) ||
    optionalString(api.normalizedUpc) ||
    optionalString(api.upc) ||
    'unknown-column';
  const itemNumber = actualItemNumber || columnKey;
  const designs = api.designs
    .map((design): MasterDesignData => {
      const crops = (design.crops ?? []).map(transformCrop);
      const persistedDesignId = optionalString(design._id) || '';
      const representativeCropId =
        optionalString(design.representativeCropId) || crops[0]?.id || null;
      const frontImageUrl = optionalString(design.frontImageUrl);
      const backImageUrl = optionalString(design.backImageUrl);
      const barcodeImageUrl = optionalString(design.barcodeImageUrl);
      const normalizedBarcode =
        optionalString(design.normalizedBarcode) ||
        optionalString(design.barcode) ||
        crops[0]?.normalizedBarcode ||
        extractBarcodeFromDesignImageUrls(barcodeImageUrl, frontImageUrl, backImageUrl);
      const notes = transformDesignNotes(design.notes);
      const designLock = transformApiDesignLockStatusInfo(design.designLock, persistedDesignId);

      return {
        id: persistedDesignId,
        persistedDesignId,
        representativeCropId,
        barcodeStatus: design.barcodeStatus,
        frontImageUrl,
        backImageUrl,
        barcodeImageUrl,
        notes,
        ...(design.isReviewed === undefined || design.isReviewed === null
          ? {}
          : { isReviewed: Boolean(design.isReviewed) }),
        reviewedBy: design.reviewedBy ?? null,
        reviewedAt: design.reviewedAt ?? null,
        ...(designLock ? { designLock } : {}),
        fullGroupReview: transformGroupReview(design.fullGroupReview),
        normalizedBarcode,
        fullGroupDesigns: (design.fullGroupDesigns ?? []).map(transformGroupSummary),
        partialGroupReview: transformGroupReview(design.partialGroupReview),
        partialGroupDesigns: (design.partialGroupDesigns ?? []).map(transformGroupSummary),
        nonFrontGroupReview: transformGroupReview(design.nonFrontGroupReview),
        nonFrontGroupDesigns: (design.nonFrontGroupDesigns ?? []).map(transformGroupSummary),
        createdAt: design.createdAt ?? null,
        createdBy: design.createdBy ?? null,
        updatedAt: design.updatedAt ?? null,
        updatedBy: design.updatedBy ?? null,
        crops,
      };
    })
    .filter((design) => design.crops.length > 0 || Boolean(design.normalizedBarcode));

  return {
    columnKey,
    upc: api.upc || lookupNormalizedBarcode || columnKey,
    normalizedUpc: api.normalizedUpc || lookupNormalizedBarcode || api.upc || columnKey,
    productName: api.productName || '',
    productImageUrl: api.productImageUrl || undefined,
    lookupStoreId: api.lookupStoreId ?? null,
    alternativeImageUrls:
      api.alternativeImageUrls && api.alternativeImageUrls.length > 0
        ? api.alternativeImageUrls
        : api.productImageUrl
          ? [api.productImageUrl]
          : undefined,
    itemNumber,
    actualItemNumber,
    lookupKind: api.lookupKind || (actualItemNumber ? 'item_number' : 'normalized_barcode'),
    lookupValue: api.lookupValue ?? null,
    lookupNormalizedBarcode: lookupNormalizedBarcode ?? null,
    barcodeSummary: api.barcodeSummary
      ? {
          primaryUpcs: api.barcodeSummary.primaryUpcs.map((entry) => ({
            upc: entry.upc,
            provenance: entry.provenance,
            storeDetails: entry.storeDetails.map((detail) => ({
              storeLabel: detail.storeLabel,
              role: detail.role,
              name: detail.name || undefined,
              casePack: detail.casePack || undefined,
              images: detail.images.map((image) => ({
                url: image.url,
                sourceLabel: image.sourceLabel || undefined,
                type: optionalString(image.type),
              })),
              isActive: detail.isActive ?? true,
            })),
          })),
          alternateUpcs: api.barcodeSummary.alternateUpcs.map((entry) => ({
            upc: entry.upc,
            provenance: entry.provenance,
            storeDetails: entry.storeDetails.map((detail) => ({
              storeLabel: detail.storeLabel,
              role: detail.role,
              name: detail.name || undefined,
              casePack: detail.casePack || undefined,
              images: detail.images.map((image) => ({
                url: image.url,
                sourceLabel: image.sourceLabel || undefined,
                type: optionalString(image.type),
              })),
              isActive: detail.isActive ?? true,
            })),
          })),
          casePacks: api.barcodeSummary.casePacks.map((entry) => ({
            upc: entry.upc,
            provenance: entry.provenance,
            storeDetails: entry.storeDetails.map((detail) => ({
              storeLabel: detail.storeLabel,
              role: detail.role,
              name: detail.name || undefined,
              casePack: detail.casePack || undefined,
              images: detail.images.map((image) => ({
                url: image.url,
                sourceLabel: image.sourceLabel || undefined,
                type: optionalString(image.type),
              })),
              isActive: detail.isActive ?? true,
            })),
          })),
        }
      : undefined,
    newDesignNormalizedBarcodes: api.newDesignNormalizedBarcodes ?? [],
    warnings: (api.warnings ?? []).map((warning) => ({
      code: warning.code,
      message: warning.message,
      designId: optionalString(warning.designId),
      normalizedBarcode: optionalString(warning.normalizedBarcode),
      resolvedVia: optionalString(warning.resolvedVia),
    })),
    snapshotVersion: api.snapshotVersion,
    designs,
    pendingCrops: api.pendingCrops.map(transformCrop),
    unassignedCrops: api.unassignedCrops.map(transformCrop),
    cartonCrops: api.cartonCrops.map(transformCrop),
    inactiveCrops: api.inactiveCrops.map(transformCrop),
  };
}
